import { proxy } from 'valtio'
import { createStoreContext, useStoreContext } from '../../shared/lib/hooks.ts'
import { ChatApi, ProgramItem } from '../../shared/api/chatApi.ts'
import { groupBy, sortBy } from '../../shared/lib/utils.ts'
import type { AppLocalStorage } from '../../shared/lib/appLocalStorage.ts'

export interface SpeakingLesson {
  tag: string
  order: number
  title: string
  type: string
  status: ProgramItem['status']
}

export interface SpeakingLessonUnit {
  title: string
  lessons: readonly SpeakingLesson[]
  index: number
}

export interface SpeakingTopic {
  title: string
  units: readonly SpeakingLessonUnit[]
  index: number
}

type VoiceLimitResponse = {
  remaining_seconds?: number | null
  limitation_date?: string
  max_duration?: number
  limit?: number
  used?: number
}

interface SpeakingState {
  remainingSeconds: number | null
  unlimitedRealtime: boolean
  limitationDate?: string
  maxDuration?: number
  loading: boolean
  error?: string
  sessionStage: 'idle' | 'connecting' | 'active' | 'ended' | 'error'
  chatId: number | null
  sessionStartTs: number | null
  isEndedFromIframe: boolean
  timerSeconds: number
  topics: SpeakingTopic[]
  selectedTopicTitle?: string
  lessonsLoading: boolean
  isPsychologySpeakingAvailable: boolean | undefined
  psychologyLessonsCompleted: number | undefined
  psychologyLessonsTotal: number | undefined
  accountLoading: boolean
}

export class SpeakingStore {
  state: SpeakingState
  private timerInterval: ReturnType<typeof setInterval> | null = null

  constructor(
    private chatApi: ChatApi,
    private appStorage: AppLocalStorage,
  ) {
    this.state = proxy<SpeakingState>({
      remainingSeconds: null,
      unlimitedRealtime: false,
      limitationDate: undefined,
      maxDuration: undefined,
      loading: false,
      error: undefined,
      sessionStage: 'idle',
      chatId: null,
      sessionStartTs: null,
      isEndedFromIframe: false,
      timerSeconds: 0,
      topics: [],
      lessonsLoading: false,
      isPsychologySpeakingAvailable: undefined,
      psychologyLessonsCompleted: undefined,
      psychologyLessonsTotal: undefined,
      accountLoading: false,
    })
  }

  async loadAccount() {
    this.state.accountLoading = true
    try {
      const s = await this.chatApi.getAccount()
      this.state.isPsychologySpeakingAvailable =
        s.is_psychology_speaking_available
      this.state.psychologyLessonsCompleted = s.psychology_lessons_completed
      this.state.psychologyLessonsTotal = s.psychology_lessons_total
    } catch (error) {
      console.error('[SPEAKING STORE] Failed to load account', error)
    } finally {
      this.state.accountLoading = false
    }
  }

  private parseRemaining(res: VoiceLimitResponse): number | null {
    if (res.remaining_seconds === null) return null
    if (typeof res.remaining_seconds === 'number') {
      return Math.max(res.remaining_seconds, 0)
    }
    const limit = res.limit ?? 0
    const used = res.used ?? 0
    return Math.max(limit - used, 0)
  }

  async loadVoiceLimit() {
    this.state.loading = true
    this.state.error = undefined
    try {
      const res = (await this.chatApi.getVoiceLimit()) as VoiceLimitResponse
      this.state.remainingSeconds = this.parseRemaining(res)
      this.state.unlimitedRealtime = res.remaining_seconds === null
      this.state.limitationDate = res.limitation_date
      this.state.maxDuration = res.max_duration
    } catch (error) {
      console.error('[SPEAKING STORE] Failed to load voice limit', error)
      this.state.error = 'load_error'
      this.state.remainingSeconds = null
      this.state.unlimitedRealtime = false
    } finally {
      this.state.loading = false
    }
  }

  private startTimer() {
    this.stopTimer()
    if (!this.state.sessionStartTs) return
    this.timerInterval = setInterval(() => {
      if (this.state.sessionStartTs) {
        const elapsed = Math.floor(
          (Date.now() - this.state.sessionStartTs) / 1000,
        )
        this.state.timerSeconds = elapsed
      }
    }, 1000)
  }

  private stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  get selectedTopic() {
    return this.state.topics.find(
      (topic) => topic.title === this.state.selectedTopicTitle,
    )
  }

  setSelectedTopic(title: string) {
    this.state.selectedTopicTitle = title
  }

  private mapTopics(items: ProgramItem[]): SpeakingTopic[] {
    const sorted = sortBy(items, (x) => x.order)
    return Object.entries(groupBy(sorted, (x) => x.display_topic)).map(
      ([title, topicItems], index) => ({
        title,
        index,
        units: Object.entries(groupBy(topicItems, (x) => x.topic)).map(
          ([topicTitle, groupItems], unitIndex) => ({
            title: topicTitle,
            index: unitIndex,
            lessons: groupItems.map((item, idx) => ({
              tag: item.tag,
              order: idx + 1,
              title: item.topic,
              type: item.lesson_type,
              status: item.status,
            })),
          }),
        ),
      }),
    )
  }

  async loadLessons(courseId?: number | null) {
    this.state.lessonsLoading = true
    try {
      const response = await this.chatApi.userProgramsRealtime(courseId)
      this.state.topics = this.mapTopics(response)
      const selectedExists = this.state.topics.some(
        (topic) => topic.title === this.state.selectedTopicTitle,
      )
      if (!selectedExists) {
        this.state.selectedTopicTitle = this.state.topics[0]?.title
      }
    } catch (error) {
      console.error('[SPEAKING STORE] Failed to load realtime programs', error)
      this.state.topics = []
      this.state.selectedTopicTitle = undefined
    } finally {
      this.state.lessonsLoading = false
    }
  }

  async startSession(userId: number, programTag: string, timestamp: number) {
    try {
      this.state.sessionStage = 'connecting'
      const courseId = this.appStorage.get('currentCourse')?.id
      if (courseId == null) throw new Error('No course is selected')
      const chat = await this.chatApi.chat_create({
        user: userId,
        program_tag: programTag,
        chat_title: `Speaking Lesson ${programTag}`,
        language: 'EN',
        parameters: { course_id: courseId },
      })
      this.state.chatId = chat.id
      this.state.sessionStartTs = timestamp
      this.state.isEndedFromIframe = false
      this.state.timerSeconds = 0
      this.state.sessionStage = 'active'
      this.startTimer()
    } catch (error) {
      console.error('[SPEAKING STORE] Failed to create chat:', error)
      this.state.sessionStage = 'error'
    }
  }

  async endSessionFromIframe(durationSec?: number) {
    if (!this.state.chatId) return
    this.state.isEndedFromIframe = true
    const duration = durationSec ?? 0
    try {
      await this.chatApi.updateVoiceLimit(this.state.chatId, { duration })
    } catch (error) {
      console.error('[SPEAKING STORE] Failed to update voice limit:', error)
    } finally {
      this.finishSession()
    }
  }

  async endSessionFallback() {
    if (this.state.isEndedFromIframe) return
    if (!this.state.chatId || !this.state.sessionStartTs) return
    const duration = Math.floor((Date.now() - this.state.sessionStartTs) / 1000)
    try {
      await this.chatApi.updateVoiceLimit(this.state.chatId, { duration })
    } catch (error) {
      console.error(
        '[SPEAKING STORE] Failed to update voice limit (fallback):',
        error,
      )
    } finally {
      this.finishSession()
    }
  }

  finishSession() {
    this.stopTimer()
    this.state.sessionStage = 'ended'
    this.state.chatId = null
    this.state.sessionStartTs = null
    this.state.timerSeconds = 0
  }

  formatSessionTimer(): string {
    const mins = Math.floor(this.state.timerSeconds / 60)
    const sec = this.state.timerSeconds % 60
    return `${mins.toString().padStart(2, '0')}:${sec
      .toString()
      .padStart(2, '0')}`
  }
}

export const SpeakingStoreContext = createStoreContext<SpeakingStore>()

export function useSpeakingStore() {
  return useStoreContext(SpeakingStoreContext)
}
